import instance from "../../axios";
import { getUserTimezone } from "../../Helpers";

/**
 * Account bank + terms for invoices: GET /api/accounts/0/terms?cn=…
 * Returns a setting-shaped object ({ attributes }) for buildBank / buildTerms.
 */
export async function fetchAccountTermsSetting(contractNumber) {
  const cn = String(contractNumber || "").trim();
  if (!cn) return null;
  try {
    const res = await instance({
      method: "GET",
      url: `/api/accounts/0/terms?cn=${encodeURIComponent(cn)}`,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });
    // instance may unwrap { status, data } → data
    if (res?.attributes) return res;
    if (res?.data?.attributes) return res.data;
    if (res?.data && typeof res.data === "object") return res.data;
    return null;
  } catch (e) {
    console.error("fetchAccountTermsSetting", e);
    return null;
  }
}

/** Resolve contract number from invoice / DN payloads. */
export function resolveContractNumber(data = {}) {
  return (
    data.contractNumber ||
    data.cn ||
    data.attributes?.c_contractNumber ||
    data.attributes?.contractNumber ||
    ""
  );
}

/**
 * Open delivery-detail (MUI Bayanat view) with autoPrint=1.
 */
export function printDeliveryNotePdf(
  item,
  {
    logInUser,
    cash = false,
    singlePo = false,
    inv,
    sDate,
    timezone,
  } = {},
) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id && !row?.attributes?.podnid) return;

  const id = singlePo ? row?.attributes?.podnid || row.id : row.id;
  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  const leasing = cash ? "true" : "false";

  let url = `/delivery-detail?&token=${id}&leasing=${leasing}&timezone=${tz}&autoPrint=1`;
  if (singlePo) {
    const invoiceNo = encodeURIComponent(inv || row.invoiceNumber || "");
    const serviceDate = encodeURIComponent(sDate || row.serviceDate || "");
    url += `&inv=${invoiceNo}&sDate=${serviceDate}&singlePo=true`;
  }

  window.open(url, "_blank");
}

/**
 * Open monthly / maintenance tax invoice (MUI) with autoPrint=1.
 */
export function printMonthlyInvoicePdf(
  item,
  { logInUser, garage = false, timezone } = {},
) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  const path = garage
    ? "maintenance-invoice-detail-garage"
    : "maintenance-invoice-detail";
  window.open(
    `/${path}?&token=${row.id}&leasing=false&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open lease invoice detail (MUI) with autoPrint=1. */
export function printLeaseInvoicePdf(item, { logInUser, timezone } = {}) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/invoiceDetail?&token=${row.id}&leasing=true&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Other Customers (cashinvoices) — cash-customer-invoice.html */
export function printCashCustomerInvoicePdf(
  item,
  { logInUser, timezone } = {},
) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/cash-customer-invoice-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open tax credit note detail (MUI) with autoPrint=1. */
export function printCreditNotePdf(item, { logInUser, timezone } = {}) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/credit-note-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open tax debit note detail (MUI) with autoPrint=1. */
export function printDebitNotePdf(item, { logInUser, timezone } = {}) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/debit-note-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open workshop purchase order (poinvoices) with autoPrint=1. */
export function printWorkshopPurchaseOrderPdf(
  item,
  { logInUser, timezone, garage = false } = {},
) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  if (garage) {
    window.open(
      `/maintenance-invoice-detail-garage?&token=${row.id}&leasing=false&timezone=${tz}&autoPrint=1`,
      "_blank",
    );
    return;
  }
  window.open(
    `/workshop-po-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open workshop payment voucher (garagepayments) with autoPrint=1. */
export function printWorkshopPaymentPdf(item, { logInUser, timezone } = {}) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/workshop-payment-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

/** Open customer receiving / receipt (payments) with autoPrint=1. */
export function printCustomerReceivingPdf(item, { logInUser, timezone } = {}) {
  const row = Array.isArray(item) ? item[0] : item;
  if (!row?.id) return;

  const tz = encodeURIComponent(timezone || getUserTimezone(logInUser));
  window.open(
    `/payment-detail?&token=${row.id}&timezone=${tz}&autoPrint=1`,
    "_blank",
  );
}

export default printDeliveryNotePdf;
